import { ConfigService, Logger } from '@nextgen-web-framework/core';
import { SocialPlateforms, UserType, useTranslation } from '@nextgen-web-framework/all';
import { sharebarApi, WebEvents, AdvanceShareCodeResponse } from '@commerce-webcomponents/sdk';
import { ChannelConfigs } from '@com.amway.commerce/webshop-configs-v1-prod/Schema';
import type { ShareUserType } from '../../components/MePanelSharebar/types/propTypes';
import logObject from '../../../logObject';
import { ShareEventsEnum, CreateSharecodeParams } from './types/types';

const logger = Logger.getInstance(logObject('sharebarHelper.ts'));

export const publishShareEvent = (linkProps?: AdvanceShareCodeResponse, sharingChannel?: SocialPlateforms) => {
  const { productData } = !linkProps?.data
    ? { productData: {} }
    : IsJsonSafe(linkProps?.data)
      ? JSON.parse(linkProps?.data)
      : { productData: {} };
  WebEvents.publishEvent({
    event: ShareEventsEnum.SHARE,
    sync: false,
    payload: {
      product_brand: [productData?.brandName ?? ''],
      share_channel: [sharingChannel ? shareEventChannel[sharingChannel] : sharingChannel],
      product_id: [productData?.productId ?? ''],
      product_name: [productData?.productName ?? ''],
      product_pointValue: [productData?.pointValue ?? ''],
      product_unitPrice: [productData?.productPrice ?? ''],
    },
  });
};
export const createSharecode = async ({
  linkProps,
  sharingChannel,
  regType,
}: CreateSharecodeParams): Promise<AdvanceShareCodeResponse | undefined> => {
  const reqPayload = {
    fullUrl: linkProps?.fullUrl,
    title: linkProps?.title,
    category: linkProps?.category,
    data: linkProps?.data,
    sharingChannel: sharingChannel,
    registrationAccountType: linkProps?.registrationAccountType ?? regType,
  };
  try {
    const response = await sharebarApi.createSharebar({
      payload: reqPayload,
    });
    const res = response as AdvanceShareCodeResponse;
    return res;
  } catch (error) {
    logger.error(error);
    return Promise.reject(error);
  }
};

export const getSharePath = (sharecode?: string, sharelinkDomain?: string, sharelinkPath?: string) => {
  const SHARELINK_PATH = 'sharelinks';
  if (sharelinkDomain) {
    return sharecode ? `${sharelinkDomain}/${sharecode}` : sharelinkDomain;
  }
  const baseUrl = ConfigService.getConfigs().NEXT_PUBLIC_WEB_BASE_PATH as string;
  const basePath = `${baseUrl}/${sharelinkPath || SHARELINK_PATH}`;
  return sharecode ? `${basePath}/${sharecode}` : basePath;
};
export const IsJsonSafe = (data: string): boolean => {
  try {
    JSON.parse(data);
    return true;
  } catch {
    return false;
  }
};

export interface SharingChannelConfig {
  type: string;
  iconName: SocialPlateforms;
  iconTitle: string;
}
export const getSocialLinksConfig = (socialIconsVisibility: ChannelConfigs, isMobile: boolean) => [
  {
    type: SocialPlateforms.LINE,
    enabled: !!socialIconsVisibility.lineUrl,
    titleKey: 'web.sharing.channel.line.title',
  },
  {
    type: SocialPlateforms.WHATSAPP,
    enabled: !!socialIconsVisibility.whatsappUrl,
    titleKey: 'web.sharing.channel.whatsapp.title',
  },
  {
    type: SocialPlateforms.FACEBOOK,
    enabled: !!socialIconsVisibility.fbUrl,
    titleKey: 'web.sharing.channel.facebook.title',
  },
  {
    type: SocialPlateforms.EMAIL,
    enabled: socialIconsVisibility.emailEnable,
    titleKey: 'web.sharing.channel.email.title',
  },
  {
    type: SocialPlateforms.LINK,
    enabled: socialIconsVisibility.copyLinkEnable,
    titleKey: 'web.sharing.channel.copylink.title',
  },
  {
    type: SocialPlateforms.MORE,
    enabled: socialIconsVisibility.moreOptionsEnable && isMobile,
    titleKey: 'web.sharing.channel.more.title',
  },
];

export const shareEventChannel = {
  [SocialPlateforms.EMAIL]: 'email',
  [SocialPlateforms.FACEBOOK]: 'facebook',
  [SocialPlateforms.LINE]: 'line',
  [SocialPlateforms.LINK]: 'link',
  [SocialPlateforms.WHATSAPP]: 'whatsapp',
  [SocialPlateforms.MORE]: 'more',
  [SocialPlateforms.TWITTER]: 'twitter',
  [SocialPlateforms.INSTAGRAM]: 'instagram',
  [SocialPlateforms.TIKTOK]: 'tiktok',
  [SocialPlateforms.LINK_COPIED]: 'copied',
};

export const useShareUserConfig = (userType: ShareUserType) => {
  const t = useTranslation();
  const ShareUserConfig = {
    [UserType.ABO]: {
      shareTitle: t('web.sharebarDrawer.abo'),
      shareDescription: t('web.sharebarDrawer.abo.desc'),
    },
    [UserType.MEMBER]: {
      shareTitle: t('web.sharebarDrawer.member'),
      shareDescription: t('web.sharebarDrawer.member.desc'),
    },
    [UserType.REGISTERED]: {
      shareTitle: t('web.sharebarDrawer.customer'),
      shareDescription: t('web.sharebarDrawer.customer.desc'),
    },
  };
  return ShareUserConfig[userType];
};
